import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Lock, LockOpen, Plus, Trash2 } from "lucide-react";
import type { SupplierDetailDto } from "@hisense/shared";
import { useBlocker, useLocation, useNavigate } from "react-router-dom";
import { PageBody, PageHeader } from "@/components/ui/PageHeader";
import { ConfirmDialog } from "@/components/ui/ConfirmDialog";
import { Button } from "@/components/ui/Button";
import { Input, FieldLabel } from "@/components/ui/Input";
import { Select } from "@/components/ui/Select";
import { useCreateSupplier, useSettings, useUpdateSupplier } from "@/api/hooks";
import { getLocalizedError } from "@/lib/api";
import { useCan, useCanSeePrivate, useDataRegion } from "@/app/access";
import { FollowersPicker } from "@/components/customers/FollowersPicker";
import { SupplierPortalPanel } from "./SupplierPortalPanel";
import { FieldGrid, FormGroup, Hint, SPAN_CLASS } from "@/components/ui/FormLayout";
import {
  SUPPLIER_CONTACT_EDGE_REM, SUPPLIER_CONTACT_FLEX_MIN_REM, SUPPLIER_CONTACT_MAIN_COLUMNS, SUPPLIER_CONTACT_SIDE_COLUMNS,
  SUPPLIER_CONTACT_WIDTH, blankSupplierContact, isBlankSupplierContact, supplierContactSideUsed,
  type SupplierContactColumn, type SupplierContactForm, type SupplierFormDraft,
} from "./supplierFormLayout";

const MAX_CONTACTS = 15;

type ContactForm = SupplierContactForm;
const blankContact = blankSupplierContact;
const isBlank = isBlankSupplierContact;

const CONTACT_LABEL: Record<SupplierContactColumn, string> = {
  name: "suppliers.contactName", phone1: "suppliers.phone1", email: "suppliers.email", specialization: "suppliers.specialization",
  phone2: "suppliers.phone2", wechat: "suppliers.wechat",
};
const CONTACT_TYPE: Partial<Record<SupplierContactColumn, "tel" | "email">> = { phone1: "tel", phone2: "tel", email: "email" };
const cellInput =
  "h-8 w-full rounded-xs border border-line bg-surface px-1.5 text-xs text-ink outline-none focus:border-brand disabled:opacity-60";
/** Cột tên dính trái khi bảng cuộn ngang; nút xoá dính phải. */
const pinLeft = "sticky left-0 z-10 bg-surface";
const pinRight = "sticky right-0 z-10 bg-surface";

export function SupplierEditorForm({
  supplier,
}: {
  supplier?: SupplierDetailDto;
}) {
  const { t } = useTranslation();
  const editing = !!supplier;
  const navigate = useNavigate();
  const { search, state } = useLocation();
  const listTo = `/suppliers${search}`;
  const detailTo = supplier ? `/suppliers/${supplier.id}${search}` : listTo;
  const returnToList = typeof state === "object" && state !== null && "returnToList" in state && state.returnToList === true;
  const cancelTo = returnToList ? listTo : detailTo;
  const [companyName, setCompanyName] = useState(supplier?.companyName ?? "");
  /*
   * Đội dùng nhà cung cấp này (29/09/2026). Tài khoản một khu vực: nhà cung cấp mới thuộc đội mình, không đổi được (máy chủ cũng
   * chặn — đổi là giấu đơn mua của đội kia). Tài khoản thấy cả hai: nhà cung cấp mới phải chọn đội; "Chưa phân loại" chỉ hiện
   * khi nhà cung cấp đang ở trạng thái đó, để giữ nguyên được chứ không để tạo thêm.
   */
  const region = useDataRegion();
  const [classification, setClassification] = useState<SupplierFormDraft["classification"]>(
    supplier ? supplier.classification ?? "unclassified" : region ?? "",
  );
  const [address, setAddress] = useState(supplier?.address ?? "");
  const [phone, setPhone] = useState(supplier?.phone ?? "");
  const [email, setEmail] = useState(supplier?.email ?? "");
  const [contacts, setContacts] = useState<ContactForm[]>(
    supplier?.contacts.length
      ? supplier.contacts.map((c) => ({
          name: c.name,
          phone1: c.phone1 ?? "",
          phone2: c.phone2 ?? "",
          email: c.email ?? "",
          wechat: c.wechat ?? "",
          specialization: c.specialization ?? "",
          isPrivate: c.isPrivate,
        }))
      : [blankContact()],
  );
  /*
   * Nhà cung cấp riêng tư và người theo dõi (Roary 09/10/2026, anh Toàn chốt 28/10/2026): chỉ quản trị viên đổi được — danh sách
   * người theo dõi chính là ai được thấy nhà cung cấp riêng tư. Người khác (kể cả người theo dõi) chỉ xem; máy chủ cũng chặn.
   */
  const canMarkPrivate = useCanSeePrivate();
  const [isPrivate, setIsPrivate] = useState(supplier?.isPrivate ?? false);
  const [followerUserIds, setFollowerUserIds] = useState<string[]>(() => supplier?.followers.map((f) => f.id) ?? []);
  const [error, setError] = useState<string | null>(null);
  /** "Hiện cột phụ" của bảng người liên hệ — có người dùng cột phụ thì cột phụ luôn hiện (supplierContactSideUsed). */
  const [sideOpen, setSideOpen] = useState(false);

  const create = useCreateSupplier();
  // useUpdateSupplier needs an id; for create mode pass a throwaway (unused).
  const update = useUpdateSupplier(supplier?.id ?? "new");
  const { data: settings } = useSettings();
  // Cổng NCC là cấp tài khoản cho người ngoài công ty — chỉ ai quản lý người dùng mới thấy.
  const canManagePortal = useCan()("users:manage");
  const busy = create.isPending || update.isPending;
  const snapshot = JSON.stringify({ companyName, classification, address, phone, email, contacts, isPrivate, followerUserIds });
  const initialSnapshot = useRef(snapshot);
  const allowNavigation = useRef(false);
  const dirty = snapshot !== initialSnapshot.current;
  const blocker = useBlocker(({ currentLocation, nextLocation }) =>
    !allowNavigation.current && (dirty || busy) &&
    (currentLocation.pathname !== nextLocation.pathname || currentLocation.search !== nextLocation.search));

  useEffect(() => {
    if (!dirty && !busy) return;
    const beforeUnload = (event: BeforeUnloadEvent) => {
      if (allowNavigation.current) return;
      event.preventDefault();
      event.returnValue = "";
    };
    window.addEventListener("beforeunload", beforeUnload);
    return () => window.removeEventListener("beforeunload", beforeUnload);
  }, [dirty, busy]);

  const specializations = settings?.businessOperations.optionLists.supplierSpecializations ?? [];

  const setContact = (i: number, patch: Partial<ContactForm>) =>
    setContacts((cs) => cs.map((c, j) => (j === i ? { ...c, ...patch } : c)));
  const sideUsed = supplierContactSideUsed(contacts);
  const showSide = sideOpen || sideUsed;
  const contactColumns: SupplierContactColumn[] = [...SUPPLIER_CONTACT_MAIN_COLUMNS, ...(showSide ? SUPPLIER_CONTACT_SIDE_COLUMNS : [])];
  const contactMinRem = SUPPLIER_CONTACT_EDGE_REM +
    contactColumns.reduce((sum, c) => sum + (SUPPLIER_CONTACT_WIDTH[c] ?? SUPPLIER_CONTACT_FLEX_MIN_REM), 0);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (busy) return;
    setError(null);
    if (!address.trim()) {
      setError(t("common.invalidField", { field: t("suppliers.address") }));
      return;
    }
    if (!classification) {
      setError(t("common.invalidField", { field: t("suppliers.classification") }));
      return;
    }

    // A row carrying data but no name would be silently discarded — refuse instead.
    const nameless = contacts.findIndex((c) => !isBlank(c) && c.name.trim() === "");
    if (nameless >= 0) {
      setError(t("common.lineError", { line: nameless + 1, field: t("suppliers.contactName") }));
      return;
    }
    const namedContacts = contacts.filter((c) => !isBlank(c));
    if (namedContacts.length === 0) {
      setError(t("common.invalidField", { field: t("suppliers.contacts") }));
      return;
    }

    const payload = {
      companyName: companyName.trim(),
      address: address.trim(),
      phone,
      email,
      contacts: namedContacts.map((c, i) => ({ ...c, sortOrder: i })),
      // Tài khoản một khu vực không gửi ô này: máy chủ tự lấy khu vực của họ cho nhà cung cấp mới và giữ nguyên khi sửa.
      ...(region ? {} : { classification: classification === "unclassified" ? null : classification }),
      // Chỉ quản trị viên gửi hai ô này; người khác không gửi thì máy chủ giữ nguyên.
      ...(canMarkPrivate ? { isPrivate, followerUserIds } : {}),
    };
    try {
      const result = editing
        ? await update.mutateAsync(payload)
        : await create.mutateAsync(payload);
      allowNavigation.current = true;
      navigate(`/suppliers/${result.data.id}${search}`, {
        replace: true,
        state: { supplierSaved: true },
      });
    } catch (err) {
      setError(getLocalizedError(err, t, "suppliers.errorSave"));
    }
  };

  return (
    <PageBody className="space-y-4">
      <ConfirmDialog
        open={blocker.state === "blocked"}
        title={t("common.unsavedTitle")}
        description={t("common.unsavedDescription")}
        confirmLabel={t("common.discardChanges")}
        cancelLabel={t("common.keepEditing")}
        tone="warn"
        busy={busy}
        onCancel={() => { if (blocker.state === "blocked") blocker.reset(); }}
        onConfirm={() => { if (blocker.state === "blocked") blocker.proceed(); }}
      />
      <PageHeader title={editing ? t("suppliers.editSupplier") : t("suppliers.newSupplier")}
        subtitle={supplier?.companyName} />
      <form onSubmit={submit} className="space-y-4">
        <fieldset disabled={busy} className="min-w-0 space-y-4">
          {/* Bố cục (19/09/2026, cùng chuẩn trang Sửa khách hàng — supplierFormLayout.ts): mỗi hàng kín 12 ô, gợi ý dưới ô. */}
          <FormGroup id="supplier-group-company" title={t("suppliers.sectionCompany")} hint={t("suppliers.companyHint")}>
            <FieldGrid>
              <div className={SPAN_CLASS[6]}>
                <FieldLabel htmlFor="s-name">{t("suppliers.company")}</FieldLabel>
                <Input id="s-name" autoFocus minLength={2} maxLength={200} required value={companyName}
                  onChange={(e) => setCompanyName(e.target.value)} />
                <Hint>{t("suppliers.required")}</Hint>
              </div>
              <div className={SPAN_CLASS[3]}>
                <FieldLabel htmlFor="s-class">{t("suppliers.classification")}</FieldLabel>
                <Select id="s-class" containerClassName="w-full" disabled={busy || !!region} value={classification}
                  onChange={(e) => setClassification(e.target.value as SupplierFormDraft["classification"])}>
                  {classification === "" ? <option value="">{t("suppliers.classificationChoose")}</option> : null}
                  <option value="vietnam">{t("suppliers.vietnam")}</option>
                  <option value="china">{t("suppliers.china")}</option>
                  {supplier && !supplier.classification ? <option value="unclassified">{t("suppliers.unclassified")}</option> : null}
                </Select>
                <Hint tone={classification === "unclassified" ? "warn" : "muted"}>
                  {region
                    ? t("suppliers.classificationLocked")
                    : classification === "unclassified"
                      ? t("suppliers.unclassifiedHint")
                      : t("suppliers.classificationHint")}
                </Hint>
              </div>
              <div className={SPAN_CLASS[3]}>
                <FieldLabel htmlFor="s-phone">{t("suppliers.phone")}</FieldLabel>
                <Input id="s-phone" type="tel" className="font-mono" maxLength={60} value={phone} onChange={(e) => setPhone(e.target.value)} />
              </div>
              <div className={SPAN_CLASS[8]}>
                <FieldLabel htmlFor="s-addr">{t("suppliers.address")}</FieldLabel>
                <Input id="s-addr" required maxLength={500} value={address} onChange={(e) => setAddress(e.target.value)} />
                <Hint>{t("suppliers.required")}</Hint>
              </div>
              <div className={SPAN_CLASS[4]}>
                <FieldLabel htmlFor="s-email">{t("suppliers.email")}</FieldLabel>
                <Input id="s-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} />
              </div>
            </FieldGrid>
            {/*
              Mã đăng nhập cổng NCC KHÔNG còn ở đây: từ 16/09/2026 mã, mật khẩu
              và khoá/mở nằm chung một bảng "Cổng nhà cung cấp" phía dưới
              (SupplierPortalPanel), gửi bằng đường API riêng. Hồ sơ mới thì
              phải lưu xong mới có gì để gắn tài khoản vào.
            */}
            {!editing && canManagePortal ? <Hint>{t("suppliers.portalAfterSave")}</Hint> : null}
          </FormGroup>
          {/* Quyền xem (0097): ai được thấy nhà cung cấp riêng tư — cùng danh sách với Settings → Users → Phân quyền. */}
          <FormGroup id="supplier-group-access" title={t("suppliers.sectionAccess")} hint={t("suppliers.accessHint")}>
            <FieldGrid>
              <div className={SPAN_CLASS[12]}>
                <FieldLabel htmlFor="s-followers">{t("suppliers.followers")}</FieldLabel>
                {canMarkPrivate ? (
                  <FollowersPicker
                    id="s-followers"
                    value={followerUserIds}
                    onChange={setFollowerUserIds}
                    region={classification === "vietnam" || classification === "china" ? classification : null}
                    known={supplier?.followers ?? []}
                    disabled={busy}
                    label={t("suppliers.followers")}
                    addLabel={t("suppliers.followerAdd")}
                    hint={isPrivate ? t("suppliers.followersPrivateHint") : t("suppliers.followersHint")}
                  />
                ) : (
                  <>
                    <p id="s-followers" className="text-sm text-ink">
                      {supplier?.followers.length ? supplier.followers.map((f) => f.name).join(", ") : t("suppliers.followersNone")}
                    </p>
                    <Hint>{t("suppliers.adminOnly")}</Hint>
                  </>
                )}
              </div>
              <div className={SPAN_CLASS[12]}>
                <label htmlFor="s-private" className="flex items-center gap-2 text-sm font-medium text-ink">
                  <input
                    id="s-private"
                    type="checkbox"
                    disabled={busy || !canMarkPrivate}
                    checked={isPrivate}
                    onChange={(e) => setIsPrivate(e.target.checked)}
                    className="size-3.5 accent-[color:var(--color-brand)]"
                  />
                  <Lock className="size-3.5 text-ink-soft" aria-hidden="true" />
                  {t("suppliers.private")}
                </label>
                <Hint>{t("suppliers.privateHint")}</Hint>
              </div>
            </FieldGrid>
          </FormGroup>
          {editing && canManagePortal ? <SupplierPortalPanel supplierId={supplier.id} asGroup /> : null}

          {/* Người liên hệ — một bảng như bảng người liên hệ của khách hàng: bốn cột chính, cột phụ ẩn khi không ai dùng. */}
          <FormGroup
            id="supplier-group-contacts"
            flush
            title={t("suppliers.contacts")}
            hint={t("suppliers.contactsCountHint", { count: contacts.filter((c) => !isBlank(c)).length, max: MAX_CONTACTS })}
            actions={
              <>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  disabled={sideUsed}
                  title={sideUsed ? t("suppliers.sideLocked") : t("suppliers.sideColumns")}
                  aria-pressed={showSide}
                  onClick={() => setSideOpen((open) => !open)}
                >
                  {showSide ? t("suppliers.sideHide") : t("suppliers.sideShow")}
                </Button>
                <Button type="button" variant="secondary" size="sm" disabled={contacts.length >= MAX_CONTACTS}
                  onClick={() => setContacts((cs) => [...cs, blankContact()])}>
                  <Plus className="size-3.5" />
                  {t("suppliers.addContact")}
                </Button>
              </>
            }
          >
            <div className="overflow-x-auto" tabIndex={0}>
              <table className="w-full table-fixed border-collapse text-sm" style={{ minWidth: `${contactMinRem}rem` }}>
                <colgroup>
                  <col style={{ width: "2rem" }} />
                  {contactColumns.map((c) => {
                    const w = SUPPLIER_CONTACT_WIDTH[c];
                    return <col key={c} style={w ? { width: `${w}rem` } : undefined} />;
                  })}
                  <col style={{ width: "2rem" }} />
                </colgroup>
                <thead>
                  <tr className="[&>th]:border-b [&>th]:border-line [&>th]:bg-sunken/60 [&>th]:px-1.5 [&>th]:py-2 [&>th]:text-left [&>th]:align-bottom [&>th]:text-2xs [&>th]:font-semibold [&>th]:uppercase [&>th]:leading-tight [&>th]:tracking-wide [&>th]:text-ink-faint">
                    <th className="!text-right">#</th>
                    {contactColumns.map((c) => (
                      <th key={c} className={c === "name" ? `${pinLeft} bg-sunken` : undefined}>{t(CONTACT_LABEL[c])}</th>
                    ))}
                    <th className={`${pinRight} bg-sunken`}><span className="sr-only">{t("common.removeContact")}</span></th>
                  </tr>
                </thead>
                <tbody>
                  {contacts.length === 0 && (
                    <tr>
                      <td colSpan={contactColumns.length + 2} className="px-4 py-6 text-center text-xs text-ink-faint">{t("suppliers.noContacts")}</td>
                    </tr>
                  )}
                  {contacts.map((c, i) => {
                    // Keep a migrated/unlisted specialization selectable so saving an old
                    // record cannot quietly drop it.
                    const legacySpec = !!c.specialization && !specializations.includes(c.specialization);
                    const aria = (key: SupplierContactColumn) => `${t(CONTACT_LABEL[key])} · ${t("suppliers.contactCard", { n: i + 1 })}`;
                    return (
                      <tr key={i} className="[&>td]:border-b [&>td]:border-line [&>td]:px-1.5 [&>td]:py-1.5">
                        <td className="text-right font-mono tabular text-2xs text-ink-faint">{String(i + 1).padStart(2, "0")}</td>
                        {contactColumns.map((key) => (
                          <td key={key} className={key === "name" ? pinLeft : undefined}>
                            {key === "specialization" ? (
                              <Select id={`s-c-${i}-spec`} aria-label={aria(key)} containerClassName="h-8 w-full" className="text-xs"
                                value={c.specialization} onChange={(e) => setContact(i, { specialization: e.target.value })}>
                                <option value="">{t("suppliers.selectSpecialization")}</option>
                                {legacySpec ? <option value={c.specialization}>{c.specialization}</option> : null}
                                {specializations.map((sp) => <option key={sp} value={sp}>{sp}</option>)}
                              </Select>
                            ) : (
                              <div className="flex items-center gap-1">
                                <input
                                  id={`s-c-${i}-${key}`}
                                  type={CONTACT_TYPE[key]}
                                  aria-label={aria(key)}
                                  // Tên bắt buộc khi hàng có dữ liệu, hoặc khi đây là người liên hệ duy nhất.
                                  required={key === "name" && (!isBlank(c) || contacts.length === 1)}
                                  value={c[key]}
                                  title={c[key] || undefined}
                                  className={`${cellInput} ${CONTACT_TYPE[key] === "tel" ? "font-mono tabular" : ""}`}
                                  onChange={(e) => setContact(i, { [key]: e.target.value })}
                                />
                                {/* Liên hệ riêng tư (0097): ô khoá trong ô tên, chỉ quản trị viên thấy — như bảng liên hệ của khách. */}
                                {key === "name" && canMarkPrivate ? (
                                  <button
                                    type="button"
                                    aria-pressed={c.isPrivate}
                                    aria-label={t("suppliers.contactPrivateToggle", { n: i + 1 })}
                                    title={t("suppliers.contactPrivate")}
                                    onClick={() => setContact(i, { isPrivate: !c.isPrivate })}
                                    className={`grid size-7 shrink-0 place-items-center rounded-xs border ${c.isPrivate ? "border-brand bg-brand text-brand-on" : "border-line text-ink-faint hover:text-ink"}`}
                                  >
                                    {c.isPrivate ? <Lock className="size-3.5" aria-hidden="true" /> : <LockOpen className="size-3.5" aria-hidden="true" />}
                                  </button>
                                ) : null}
                              </div>
                            )}
                          </td>
                        ))}
                        <td className={`text-center ${pinRight}`}>
                          <button type="button" onClick={() => setContacts((cs) => cs.filter((_, j) => j !== i))}
                            aria-label={`${t("common.removeContact")} ${i + 1}`} className="text-ink-faint hover:text-danger">
                            <Trash2 className="size-3.5" />
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </FormGroup>
        </fieldset>
        {error && (
          <p role="alert" className="rounded-sm bg-danger-tint px-3 py-2 text-sm text-danger">{error}</p>
        )}
        {/* Một thanh Hủy / Lưu dính đáy, cùng kiểu các trang sửa khác. */}
        <div className="sticky bottom-0 z-20 flex flex-wrap items-center justify-end gap-x-3 gap-y-2 rounded-md border border-line bg-surface px-4 py-3 shadow-[var(--shadow-card)]">
          <p className="mr-auto text-xs text-ink-faint">{t("suppliers.saveHint")}</p>
          <Button type="button" variant="secondary" disabled={busy} onClick={() => navigate(cancelTo)}>
            {t("common.cancel")}
          </Button>
          <Button type="submit" disabled={busy || companyName.trim().length < 2}>
            {busy ? t("common.saving") : t("common.save")}
          </Button>
        </div>
      </form>
    </PageBody>
  );
}
